import { Box, Stack } from "@mui/material";
import { ReactNode } from "react";

import { FileItem } from "@/common/fileItem";

import ImageComparisonCenterStage from "./ImageComparisonCenterStage";
import { ImageComparisonSidePanel } from "./ImageComparisonSidePanel";
import { useImageComparisonState } from "./useImageComparisonState";

interface BeforeAfter {
  title: ReactNode;
  subtitle?: ReactNode;
  label?: ReactNode;
  images: FileItem[];
}

export interface ImageComparisonWorkspaceProps {
  topSlot?: ReactNode;
  before: BeforeAfter;
  after: BeforeAfter;
}

export function ImageComparisonWorkspace({
  topSlot,
  before,
  after,
}: ImageComparisonWorkspaceProps) {
  const state = useImageComparisonState({
    beforeImages: before.images,
    afterImages: after.images,
  });

  return (
    <Stack spacing={1} sx={{ height: "100%", minHeight: 0 }}>
      {topSlot && <Box>{topSlot}</Box>}

      <Box
        sx={{
          display: "grid",
          gap: 2,
          height: "100%",
          minHeight: 0,
          gridTemplateAreas: {
            xxs: `"before" "after" "center"`,
            md: `"before center after"`,
          },
          gridTemplateColumns: {
            xxs: "1fr",
            md: "fit-content(140px) 1fr fit-content(140px)",
            lg: "fit-content(140px) 1fr fit-content(140px)",
            xl: "fit-content(240px) 1fr fit-content(240px)",
            xxl: "fit-content(320px) 1fr fit-content(320px)",
          },
          gridTemplateRows: {
            xxs: "max-content max-content max-content",
            md: "1fr",
          },
        }}
      >
        <ImageComparisonSidePanel
          sx={{ gridArea: "before" }}
          title={before.title}
          subtitle={before.subtitle}
          images={before.images}
          selectedId={state.beforeSelectedId}
          onSelect={(f) => state.setBeforeSelectedId(f.id)}
        />

        <ImageComparisonCenterStage
          sx={{ gridArea: "center" }}
          before={state.beforeSelected}
          after={state.afterSelected}
          beforeLabel={before.label}
          afterLabel={after.label}
          isShowLabels={state.isShowLabels}
          isSwapped={state.isSwapped}
          onToggleLabels={() => state.setIsShowLabels((x) => !x)}
          onToggleSwap={() => state.setIsSwapped((x) => !x)}
        />

        <ImageComparisonSidePanel
          sx={{ gridArea: "after" }}
          title={after.title}
          subtitle={after.subtitle}
          images={after.images}
          selectedId={state.afterSelectedId}
          onSelect={(f) => state.setAfterSelectedId(f.id)}
        />
      </Box>
    </Stack>
  );
}
